import React, { useEffect, useRef, useState } from "react";
import { Modal, Button } from "react-bootstrap";

// Tags de contato (27/10/2026). Desde 29/10 cada tag é de um TIME do cliente: o time só vê
// e usa as próprias; o admin master vê todas, separadas por time.
// Rotas no backend: /tags, /tags/<id>, /tags/vincular.

export interface ContactTag {
  id: number;
  nome: string;
  cor: string; // #rrggbb
  id_time?: number | null;
  nome_time?: string | null;
}

export interface TagTime {
  id: string;
  nome: string;
}

export const TAG_CORES_SUGERIDAS = [
  "#ef4444", "#f97316", "#f59e0b", "#eab308", "#22c55e", "#14b8a6",
  "#06b6d4", "#3b82f6", "#6366f1", "#8b5cf6", "#d946ef", "#ec4899",
  "#64748b", "#78716c",
];

// Texto escuro em cor clara e branco em cor escura (luminância relativa simples).
export function corTextoTag(hex: string): string {
  const h = (hex || "").replace("#", "");
  if (h.length !== 6) return "#fff";
  const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.62 ? "#1f2937" : "#fff";
}

export const TagChip: React.FC<{ tag: ContactTag; tamanho?: "card" | "header"; onRemover?: () => void }> = ({
  tag, tamanho = "card", onRemover,
}) => (
  <span
    title={tag.nome}
    style={{
      fontSize: tamanho === "card" ? "10px" : "11.5px",
      backgroundColor: tag.cor,
      color: corTextoTag(tag.cor),
      padding: tamanho === "card" ? "2px 8px" : "3px 10px",
      borderRadius: "6px",
      fontWeight: 600,
      letterSpacing: "0.3px",
      textTransform: "uppercase",
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      whiteSpace: "nowrap",
      maxWidth: 160,
      overflow: "hidden",
      textOverflow: "ellipsis",
    }}
  >
    {tag.nome}
    {onRemover && (
      <i
        className="ti ti-x"
        role="button"
        title="Tirar a tag"
        onClick={(e) => { e.stopPropagation(); onRemover(); }}
        style={{ fontSize: 11, cursor: "pointer", opacity: 0.85 }}
      />
    )}
  </span>
);

// Seletor de cor: paleta sugerida + o color picker nativo para qualquer outra cor.
const SeletorCor: React.FC<{ valor: string; onChange: (c: string) => void }> = ({ valor, onChange }) => (
  <div className="d-flex flex-wrap align-items-center gap-1">
    {TAG_CORES_SUGERIDAS.map((c) => (
      <button
        key={c}
        type="button"
        title={c}
        onClick={() => onChange(c)}
        style={{
          width: 22, height: 22, borderRadius: "50%", background: c, padding: 0, cursor: "pointer",
          border: valor.toLowerCase() === c ? "2px solid #111827" : "2px solid transparent",
          boxShadow: valor.toLowerCase() === c ? "0 0 0 2px #fff inset" : "none",
        }}
      />
    ))}
    <input
      type="color"
      value={valor}
      onChange={(e) => onChange(e.target.value)}
      title="Outra cor"
      style={{ width: 28, height: 24, padding: 0, border: "none", background: "transparent", cursor: "pointer" }}
    />
  </div>
);

// Modal "Tags": lista as tags (por time), cria, edita (nome/cor) e apaga.
// `times` = times que o login gerencia (1 para o operador; todos para o admin master).
export const TagsManagerModal: React.FC<{
  show: boolean;
  onHide: () => void;
  tags: ContactTag[];
  times: TagTime[];
  usoPorTag: Record<number, number>;
  onCriar: (nome: string, cor: string, idTime: string) => Promise<string | null>;
  onEditar: (id: number, nome: string, cor: string) => Promise<string | null>;
  onApagar: (id: number) => Promise<string | null>;
}> = ({ show, onHide, tags, times, usoPorTag, onCriar, onEditar, onApagar }) => {
  const [nome, setNome] = useState("");
  const [cor, setCor] = useState(TAG_CORES_SUGERIDAS[8]);
  const [timeNova, setTimeNova] = useState<string>("");
  const timeEscolhido = timeNova || times[0]?.id || "";
  // Grupos na ordem dos times; tag sem time (antiga) vai num grupo próprio no fim.
  const grupos = [
    ...times.map((t) => ({ id: t.id, nome: t.nome, tags: tags.filter((g) => String(g.id_time ?? "") === t.id) })),
    { id: "", nome: "Sem time", tags: tags.filter((g) => g.id_time == null) },
  ].filter((g) => g.tags.length > 0 || (times.length > 1 && g.id !== ""));
  const [editId, setEditId] = useState<number | null>(null);
  const [editNome, setEditNome] = useState("");
  const [editCor, setEditCor] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!show) { setEditId(null); setErro(null); setNome(""); }
  }, [show]);

  const criar = async () => {
    if (!nome.trim() || salvando) return;
    setSalvando(true); setErro(null);
    if (!timeEscolhido) { setSalvando(false); setErro("Escolha o time da tag."); return; }
    const e = await onCriar(nome.trim(), cor, timeEscolhido);
    setSalvando(false);
    if (e) setErro(e); else setNome("");
  };

  const salvarEdicao = async () => {
    if (editId == null || !editNome.trim() || salvando) return;
    setSalvando(true); setErro(null);
    const e = await onEditar(editId, editNome.trim(), editCor);
    setSalvando(false);
    if (e) setErro(e); else setEditId(null);
  };

  const apagar = async (t: ContactTag) => {
    const n = usoPorTag[t.id] || 0;
    const aviso = n ? `\n\nEla está em ${n} contato${n === 1 ? "" : "s"} e sai de todos.` : "";
    if (!window.confirm(`Apagar a tag "${t.nome}"?${aviso}`)) return;
    setSalvando(true); setErro(null);
    const e = await onApagar(t.id);
    setSalvando(false);
    if (e) setErro(e);
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title><i className="ti ti-tags me-2" />Tags</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <label className="form-label fw-semibold mb-1">
            Nova tag{times.length === 1 ? <span className="text-muted fw-normal"> · {times[0].nome}</span> : null}
          </label>
          {times.length > 1 && (
            <select
              className="form-select form-select-sm mb-2"
              value={timeEscolhido}
              onChange={(e) => setTimeNova(e.target.value)}
              title="Time dono da tag"
            >
              {times.map((t) => <option key={t.id} value={t.id}>{t.nome}</option>)}
            </select>
          )}
          <div className="d-flex gap-2 mb-2">
            <input
              className="form-control"
              placeholder="Nome da tag"
              maxLength={40}
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") criar(); }}
            />
            <Button variant="primary" onClick={criar} disabled={!nome.trim() || salvando}>Criar</Button>
          </div>
          <SeletorCor valor={cor} onChange={setCor} />
          {nome.trim() && (
            <div className="mt-2">
              <small className="text-muted me-2">Prévia:</small>
              <TagChip tag={{ id: 0, nome: nome.trim(), cor }} tamanho="header" />
            </div>
          )}
        </div>

        {erro && <div className="alert alert-danger py-2 mb-3">{erro}</div>}

        <label className="form-label fw-semibold mb-1">Tags criadas ({tags.length})</label>
        {tags.length === 0 && grupos.length === 0 ? (
          <div className="text-muted small">Nenhuma tag ainda.</div>
        ) : (
          <div className="d-flex flex-column gap-2" style={{ maxHeight: 360, overflowY: "auto" }}>
            {grupos.map((g) => (
              <React.Fragment key={g.id || "sem-time"}>
                {(times.length > 1 || g.id === "") && (
                  <div className="small fw-bold text-uppercase text-muted mt-1" style={{ letterSpacing: 0.4 }}>
                    {g.nome} <span className="fw-normal">({g.tags.length})</span>
                  </div>
                )}
                {g.tags.length === 0 && <div className="text-muted small ps-1">Nenhuma tag neste time.</div>}
            {g.tags.map((t) => (
              <div key={t.id} className="border rounded p-2">
                {editId === t.id ? (
                  <>
                    <div className="d-flex gap-2 mb-2">
                      <input
                        className="form-control form-control-sm"
                        maxLength={40}
                        value={editNome}
                        onChange={(e) => setEditNome(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") salvarEdicao(); }}
                        autoFocus
                      />
                      <Button size="sm" variant="primary" onClick={salvarEdicao} disabled={salvando}>Salvar</Button>
                      <Button size="sm" variant="secondary" onClick={() => setEditId(null)}>Cancelar</Button>
                    </div>
                    <SeletorCor valor={editCor} onChange={setEditCor} />
                  </>
                ) : (
                  <div className="d-flex align-items-center gap-2">
                    <TagChip tag={t} tamanho="header" />
                    <small className="text-muted flex-grow-1">
                      {usoPorTag[t.id] || 0} contato{(usoPorTag[t.id] || 0) === 1 ? "" : "s"}
                    </small>
                    <button
                      type="button" className="btn btn-sm btn-light" title="Editar"
                      onClick={() => { setEditId(t.id); setEditNome(t.nome); setEditCor(t.cor); setErro(null); }}
                    >
                      <i className="ti ti-pencil" />
                    </button>
                    <button type="button" className="btn btn-sm btn-light text-danger" title="Apagar" onClick={() => apagar(t)}>
                      <i className="ti ti-trash" />
                    </button>
                  </div>
                )}
              </div>
            ))}
              </React.Fragment>
            ))}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Fechar</Button>
      </Modal.Footer>
    </Modal>
  );
};

// Linha de tags no header da conversa: chips do contato (com "x" para tirar) + botão
// "Tag" que abre a lista de tags do cliente para marcar/desmarcar.
export const HeaderTags: React.FC<{
  tagsDoContato: ContactTag[];
  todasTags: ContactTag[];
  onAlternar: (tag: ContactTag, ativo: boolean) => void;
  onGerenciar: () => void;
}> = ({ tagsDoContato, todasTags, onAlternar, onGerenciar }) => {
  const [aberto, setAberto] = useState(false);
  // O nome/fone do header fica num contêiner com overflow hidden: o menu usa position fixed
  // na posição do botão para não ser cortado.
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const ref = useRef<HTMLDivElement>(null);
  const ids = new Set(tagsDoContato.map((t) => t.id));

  const abrir = () => {
    const r = ref.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + 4, left: Math.max(8, Math.min(r.left, window.innerWidth - 240)) });
    setAberto((v) => !v);
  };

  useEffect(() => {
    if (!aberto) return;
    const fora = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setAberto(false); };
    const fechar = () => setAberto(false);
    document.addEventListener("mousedown", fora);
    window.addEventListener("resize", fechar);
    return () => { document.removeEventListener("mousedown", fora); window.removeEventListener("resize", fechar); };
  }, [aberto]);

  return (
    <div className="d-flex flex-wrap align-items-center gap-1 mt-1">
      {tagsDoContato.map((t) => (
        <TagChip key={t.id} tag={t} tamanho="header" onRemover={() => onAlternar(t, false)} />
      ))}
      <div ref={ref} className="position-relative">
        <button
          type="button"
          onClick={abrir}
          style={{
            fontSize: 11.5, fontWeight: 600, borderRadius: 6, padding: "2px 8px", cursor: "pointer",
            border: "1px dashed rgba(100,116,139,0.6)", background: "transparent", color: "#64748b",
          }}
        >
          <i className="ti ti-plus" style={{ fontSize: 11 }} /> Tag
        </button>
        {aberto && (
          <div
            className="dropdown-menu show shadow"
            style={{ position: "fixed", top: pos.top, left: pos.left, zIndex: 1060, minWidth: 220, maxHeight: 300, overflowY: "auto", padding: 6 }}
          >
            {todasTags.length === 0 && <div className="text-muted small px-2 py-1">Nenhuma tag criada.</div>}
            {todasTags.map((t) => {
              const marcada = ids.has(t.id);
              return (
                <button
                  key={t.id}
                  type="button"
                  className="dropdown-item d-flex align-items-center gap-2 rounded"
                  onClick={() => onAlternar(t, !marcada)}
                >
                  <i className={`ti ${marcada ? "ti-square-check-filled" : "ti-square"}`} style={{ color: marcada ? t.cor : "#94a3b8" }} />
                  <TagChip tag={t} />
                </button>
              );
            })}
            <div className="dropdown-divider" />
            <button type="button" className="dropdown-item small" onClick={() => { setAberto(false); onGerenciar(); }}>
              <i className="ti ti-settings me-1" /> Criar / editar tags
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
